import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Sidebar from "../../../sidebar/sidebar";
import Header from "../../../header/header";
import KpiPageHeader from "../../Quantative/component/KpiPageHeader";
import KpiFiltersBar from "../../Quantative/component/Kpifiltersbar";
import BulkActionBar from "../../Quantative/component/BulkActionBar";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";
import QualitativeRow from "./QualitativeRow";
import QualitativeAddDataModal from "./QualitativeAddDataModal";
import { injectKpiStyles } from "../../Quantative/component/kpiStyles";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";
import { usePermission } from "../../../../hooks/usePermission";
import { MasterDataProvider } from "../context/MasterDataContext";
import { useQualitativeMeta} from "../hooks/useQualitativeMeta"
import { useQualitativeCapabilities } from "../hooks/useQualitativeCapabilities";
import { fetchAnsweredQuestionIds } from "../services/answeredStatus.service";
import { useKpiLocks } from "../../KpiLock/useKpiLocks";
import { hasPendingRequest, lockFor, pendingRequestIds, wasAccessDeclined } from "../../KpiLock/helpers";

// Bulk Action Modals
import QualitativeOwnerAssignModal from "./BulkOwnerAssign/QualitativeOwnerAssignModal";
import BulkReassignModal from "../../Quantative/component/bulkAction/Bulkreassignmodal";
import BulkPrefillModal from "../../Quantative/component/bulkAction/Bulkprefillmodal";
import BulkReminderModal from "../../Quantative/component/bulkAction/Bulkremindermodal";
import BulkAuditAssignModal from "../../Quantative/component/bulkAction/BulkAuditAssign/BulkAuditAssignModal";

// Capabilities BulkActionBar checks for its assign-data-owner / assign-auditor /
// email-reminder actions. Only these are gated here — prefill is gated separately
// on qualitative-data.create via disabledActions.
const BULK_GATED_CAPS = ["data-owner.assign", "data-owner.remove", "auditor.assign", "email-reminder.send"];

// qualitative-data/assignments returns camelCase users; the AssignedTo/By avatar
// cells (shared with Quantative) render snake_case fields.
const toAssigneeUser = (u) => ({
    id: u.id,
    first_name: u.firstName,
    last_name: u.lastName,
    email: u.email,
    name: [u.firstName, u.lastName].filter(Boolean).join(" "),
});

const Qualitative = () => {
    const { companyId, currentUserId } = useCurrentUser();
    const { financialYears, modules, frameworks, locations, metaLoading } = useQualitativeMeta();
    const { canCreate } = useQualitativeCapabilities();
    const { hasPermission } = usePermission();
    const bulkHasPermission = (cap) => {
        // qualitative has no reassign capability of its own — reassign rides on data-owner.assign
        if (cap === "data-owner.reassign") return hasPermission("data-owner.assign");
        return BULK_GATED_CAPS.includes(cap) ? hasPermission(cap) : true;
    };

    /**
     * Whether this user sees every question's row, not just their own assignments.
     * Mirrors the backend assignment scoping: getQualitativeAssignmentsByFinancialYear
     * returns all assignees only to data-owner.assign holders (admins included via the
     * capability wildcard) and just the caller's own assignments otherwise. Gating row
     * visibility on the same capability keeps the visible rows consistent with the
     * assignment data the API actually returned.
     */
    const canSeeAllRows = hasPermission("data-owner.assign");

    const [sidebarExpanded, setSidebarExpanded] = useState(true);
    const [questions, setQuestions] = useState([]);
    // ids of questions with a non-empty answer for the selected FY — drives the
    // row status pill and the header done/pending counts
    const [answeredIds, setAnsweredIds] = useState(() => new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [expandedRows, setExpandedRows] = useState({});
    const [selectedRows, setSelectedRows] = useState({});
    const [bulkModal, setBulkModal] = useState(null);
    // single-question prefill opened from a row: { id, refresh }
    const [prefillRow, setPrefillRow] = useState(null);
    const [managementListValue, setManagementListValue] = useState([]);
    const [auditorType, setAuditorType] = useState([]);
    // assignments endpoint answered 403 — user may not see who questions are assigned to/by
    const [assignmentsForbidden, setAssignmentsForbidden] = useState(false);
    
    // Default filters
    const [searchQuery, setSearchQuery] = useState("");
    const [moduleFilter, setModuleFilter] = useState("");
    const [frameworkFilter, setFrameworkFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [fyFilter, setFyFilter] = useState("");
    const [financialYearId, setFinancialYearId] = useState("");
    const [rangeInput, setRangeInput] = useState("");
    const [rangeError, setRangeError] = useState("");
    
    // Modal state
    const [modalConfig, setModalConfig] = useState({
        show: false,
        question: null,
        matchedAnswer: null,
        onSaveComplete: null
    });

    useEffect(() => {
        injectKpiStyles();
    }, []);

    // Meta fetch finished without any financial years (e.g. getFinancialYear failed) —
    // fetchQuestions can never fire, so stop the page loader and show the empty state.
    useEffect(() => {
        if (!metaLoading && (!financialYears || financialYears.length === 0)) {
            setIsLoading(false);
        }
    }, [metaLoading, financialYears]);

    useEffect(() => {
        if (financialYears && financialYears.length > 0 && !fyFilter) {
            const lastId = financialYears[financialYears.length - 1].id;
            setFyFilter(String(lastId));
            setFinancialYearId(lastId);
        }
    }, [financialYears, fyFilter]);

    // Handle filter changes — keeps fyFilter (string for the select) and financialYearId (number for API) in sync
    const handleFyChange = (val) => {
        setFyFilter(val);
        setFinancialYearId(val ? parseInt(val) : "");
    };

    const fetchFrameworkApi = useCallback(async () => {
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}getFramework`,
                {},
                { type: "ALL" },
                "GET",
                true,
                false
            );
            if (isSuccess) {
                return (data?.data || []).map((item) => item.id);
            }
        } catch (error) {
            console.error("Error fetching framework:", error);
        }
        return [];
    }, []);

    const fetchAssignments = useCallback(async () => {
        try {
            const { isSuccess, status, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}qualitative-data/assignments/${financialYearId}`,
                {},
                {},
                "GET",
                true,
                false
            );
            if (isSuccess) {
                setAssignmentsForbidden(false);
                return data?.data?.assignments || [];
            }
            setAssignmentsForbidden(status === 403);
        } catch (error) {
            console.error("Error fetching qualitative assignments:", error);
        }
        return [];
    }, [financialYearId]);

    const fetchIdRef = useRef(0);

    /**
     * Fetches questions + assignments for the selected FY. background=true
     * refreshes silently after a mutation — no blocking loader, rows keep
     * their expanded/selection state, and a transient failure keeps the
     * current list instead of blanking the page. A fetch-id guard drops
     * stale responses when a newer fetch has started.
     */
    const fetchQuestions = useCallback(async ({ background = false } = {}) => {
        if (!financialYearId) return;
        const fetchId = ++fetchIdRef.current;
        if (!background) setIsLoading(true);
        try {
            const frameworkIdsToFetch = await fetchFrameworkApi();
            const frameworkIds = frameworkIdsToFetch.length > 0 ? frameworkIdsToFetch : [1];

            const [{ isSuccess, data }, assignments, answered] = await Promise.all([
                apiCall(
                    `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`,
                    {},
                    {
                      financialYearId,
                      frameworkIds: JSON.stringify(frameworkIds),
                      question_type_filter: 'tabular_question',
                      fromSettings: true
                    },
                    "GET",
                    true,
                    false
                ),
                fetchAssignments(),
                fetchAnsweredQuestionIds(financialYearId),
            ]);

            if (fetchId !== fetchIdRef.current) return;

            setAnsweredIds(new Set(answered));

            if (isSuccess && data?.data) {
                const assignmentMap = assignments.reduce((acc, item) => {
                    acc[item.questionId] = item;
                    return acc;
                }, {});

                const tabularQuestions = (data.data || []).filter(q => q.questionType === 'tabular_question');
                const merged = tabularQuestions
                    .map(q => {
                        const assignment = assignmentMap[q.questionId || q.id];
                        return {
                            ...q,
                            description: q.description ?? null,
                            assigned_to: (assignment?.users || []).map(toAssigneeUser),
                            assigned_by: assignment?.assignedBy ? [toAssigneeUser(assignment.assignedBy)] : []
                        };
                    });
                setQuestions(merged);
            } else if (!background) {
                setQuestions([]);
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
            if (fetchId === fetchIdRef.current && !background) setQuestions([]);
        } finally {
            if (fetchId === fetchIdRef.current) setIsLoading(false);
        }
    }, [financialYearId, fetchFrameworkApi, fetchAssignments]);

    useEffect(() => {
        if (financialYearId) fetchQuestions();
    }, [financialYearId, fetchQuestions]);

    const toggleRowExpand = (id) => {
        setExpandedRows(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(k => next[k] = false);
            next[id] = !prev[id];
            return next;
        });
    };

    const toggleRowSelection = (id) => {
        setSelectedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleDeselectAll = () => setSelectedRows({});

    const handleRangeClear = () => {
        setRangeInput("");
        setRangeError("");
    };

    const handleRangeApply = () => {
        setRangeError("");
        if (!rangeInput.trim()) {
            setRangeError("Please enter a valid range first.");
            return;
        }

        const parts = rangeInput.split(",");
        const indices = new Set();
        const maxLen = filteredQuestions.length;

        for (let part of parts) {
            part = part.trim();
            if (!part) continue;

            if (/^\d+$/.test(part)) {
                const idx = parseInt(part, 10);
                if (idx < 1 || idx > maxLen) {
                    setRangeError(`Row ${idx} is out of range (total rows: ${maxLen}).`);
                    return;
                }
                indices.add(idx);
            } else if (/^\d+-\d+$/.test(part)) {
                const [startStr, endStr] = part.split("-");
                const start = parseInt(startStr, 10);
                const end = parseInt(endStr, 10);
                if (start < 1 || end < 1 || start > maxLen || end > maxLen) {
                    setRangeError(`Range ${part} is out of range (total rows: ${maxLen}).`);
                    return;
                }
                if (start > end) {
                    setRangeError(`Invalid range: ${part}. Start row must be less than or equal to end row.`);
                    return;
                }
                for (let i = start; i <= end; i++) {
                    indices.add(i);
                }
            } else {
                setRangeError(`Invalid format: "${part}". Use numbers and ranges, e.g. "1-3, 5".`);
                return;
            }
        }

        if (indices.size === 0) {
            setRangeError("No valid rows matched.");
            return;
        }

        const newRows = {};
        [...indices].forEach(idx => {
            const q = filteredQuestions[idx - 1];
            const id = q.questionId || q.id;
            newRows[id] = true;
        });
        setSelectedRows(newRows);
        setRangeError("");
    };

    const handleBulkAction = (actionKey) => {
        setBulkModal(actionKey);
    };

    const closeBulkModal = () => setBulkModal(null);

    const handleAddData = (question, matchedAnswer, refetchAnswer) => {
        setModalConfig({
            show: true,
            question,
            matchedAnswer,
            onSaveComplete: () => {
                refetchAnswer && refetchAnswer();
                fetchQuestions({ background: true });
            }
        });
    };

    const closeAddDataModal = () => {
        setModalConfig({
            show: false,
            question: null,
            matchedAnswer: null,
            onSaveComplete: null
        });
    };

    const uniqueModuleNames = useMemo(() => {
        return Array.from(new Set(questions.map(q => q.moduleName).filter(Boolean))).sort();
    }, [questions]);

    // Questions are fetched per selected FY, so deriving the dropdown from the
    // loaded questions would collapse it to the current selection and make
    // switching years impossible.
    const availableFinancialYears = financialYears || [];

    const handleClearAll = () => {
        setSearchQuery("");
        setModuleFilter("");
        setFrameworkFilter("");
        setStatusFilter("");
        setRangeInput("");
        setRangeError("");
        if (financialYears?.length > 0) {
            const lastFY = financialYears[financialYears.length - 1];
            setFyFilter(String(lastFY.id));
            setFinancialYearId(lastFY.id);
        }
    };

    const isAnswered = useCallback(
        (q) => answeredIds.has(Number(q.questionId || q.id)),
        [answeredIds],
    );

    // All filters except status — the header done/pending breakdown is derived
    // from this so the counts stay stable when a status filter is applied.
    /**
     * Base row visibility: users without data-owner.assign see only questions they
     * are assigned to (their id present in assigned_to); data-owner.assign holders
     * (admins included) see every row. Search / module / framework filters apply on
     * top of this.
     */
    const scopedQuestions = useMemo(() => {
        let result = questions;
        if (!canSeeAllRows) {
            result = result.filter(q =>
                (q.assigned_to || []).some(u => Number(u.id) === Number(currentUserId))
            );
        }
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(item => (item.title || "").toLowerCase().includes(q));
        }
        if (moduleFilter) {
            result = result.filter(q => q.moduleName === moduleFilter);
        }
        if (frameworkFilter) {
            const fwId = Number(frameworkFilter);
            result = result.filter(q => {
                try {
                    const ids = JSON.parse(q.mapFrameworkIds || "[]");
                    return ids.includes(fwId);
                } catch {
                    return false;
                }
            });
        }

        return result;
    }, [questions, searchQuery, moduleFilter, frameworkFilter, canSeeAllRows, currentUserId]);

    const filteredQuestions = useMemo(() => {
        if (statusFilter === "answered") return scopedQuestions.filter(isAnswered);
        if (statusFilter === "unanswered") return scopedQuestions.filter(q => !isAnswered(q));
        return scopedQuestions;
    }, [scopedQuestions, statusFilter, isAnswered]);

    const answeredCount = useMemo(
        () => scopedQuestions.filter(isAnswered).length,
        [scopedQuestions, isAnswered],
    );
    const pendingCount = scopedQuestions.length - answeredCount;

    const selectedIds = useMemo(() => Object.keys(selectedRows).filter(id => selectedRows[id]).map(Number), [selectedRows]);
    /**
     * Lock state for the questions on screen.
     *
     * Qualitative answers carry no location, so no locations are passed — the
     * deadline applies to the KPI's answer for the whole financial year.
     */
    const { locks, requests, requestAccess, approveAccess, rejectAccess } = useKpiLocks({
        financialYearId,
        questionIds: filteredQuestions.map(q => q.questionId || q.id),
        locationIds: [],
        questionnaireType: "QUAL",
    });

    const [requestingAccessId, setRequestingAccessId] = useState(null);
    const [accessDecisionError, setAccessDecisionError] = useState(null);

    /** Whether this user decides access requests rather than raising them. */
    const canDecideAccess = hasPermission('due-dates.approve');

    /**
     * Asks an administrator to reopen a locked qualitative KPI.
     * @param {object} question the KPI row.
     * @param {object} lock the KPI's lock state.
     */
    const handleRequestAccess = useCallback(async (question, lock) => {
        const kpiId = question.questionId || question.id;
        setRequestingAccessId(kpiId);
        await requestAccess({ kpiId, lock });
        setRequestingAccessId(null);
    }, [requestAccess]);

    /**
     * Grants an extension on a KPI's outstanding requests.
     * @param {number[]} ids the requests to approve.
     * @param {number} days how many days to reopen entry for.
     */
    const handleApproveAccess = useCallback(async (ids, days) => {
        setAccessDecisionError(null);
        const res = await approveAccess(ids, days);
        if (!res.ok) setAccessDecisionError(res.message || "Could not approve the request.");
    }, [approveAccess]);

    /**
     * Declines a KPI's outstanding requests, leaving it locked.
     * @param {number[]} ids the requests to reject.
     */
    const handleRejectAccess = useCallback(async (ids) => {
        setAccessDecisionError(null);
        const res = await rejectAccess(ids);
        if (!res.ok) setAccessDecisionError(res.message || "Could not reject the request.");
    }, [rejectAccess]);

    const isAllSelected = filteredQuestions.length > 0 && filteredQuestions.every(q => selectedRows[q.questionId || q.id]);

    const selectedFyObj = useMemo(() => {
        const found = financialYears?.find(fy => fy.id === financialYearId);
        return { id: financialYearId, name: found ? (found.financial_year_value || found.name) : fyFilter };
    }, [financialYears, financialYearId, fyFilter]);

    const ColLabel = ({ children, width, flex, align = "left" }) => (
        <div style={{
            ...(width ? { width, flexShrink: 0 } : {}),
            ...(flex ? { flex } : {}),
            padding: "0 10px",
            fontSize: 10,
            fontWeight: 700,
            color: "var(--ink-4)",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            fontFamily: "var(--font-mono)",
            textAlign: align,
        }}>
            {children}
        </div>
    );

    return (
        <MasterDataProvider>
            <div style={{ display: "flex", background: "#f0f2f5", minHeight: "100vh", fontFamily: "var(--font-sans)" }}>
                <div style={{ flex: sidebarExpanded ? "0 0 21%" : "0 0 60px", position: "sticky", top: 0, height: "100vh", background: "var(--surface)", borderRight: "1px solid var(--rule)", zIndex: 100, transition: "flex 0.2s ease" }}>
                    <Sidebar dataFromParent="/data-management/qualitative" onSidebarToggle={setSidebarExpanded} />
                </div>

                {/* overflow: clip (not hidden) — hidden would make this the sticky
                    containment box and the bulk bar could never pin to the window */}
                <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "clip", minWidth: 0 }}>
                    <Header />
                    <KpiPageHeader
                        pageTitle="Qualitative KPI"
                        selectedFY={selectedFyObj}
                        totalCount={scopedQuestions.length}
                        completedCount={answeredCount}
                        pendingCount={pendingCount}
                        selectedLocation={[]}
                        advFilterCount={0}
                    />

                    <KpiFiltersBar
                        search={searchQuery}
                        onSearchChange={setSearchQuery}
                        moduleFilter={moduleFilter}
                        onModuleChange={setModuleFilter}
                        fyFilter={fyFilter}
                        onFyChange={handleFyChange}
                        frameworkFilter={frameworkFilter}
                        onFrameworkChange={setFrameworkFilter}
                        statusFilter={statusFilter}
                        onStatusChange={setStatusFilter}
                        onClearAll={handleClearAll}
                        financialYears={availableFinancialYears}
                        modules={uniqueModuleNames}
                        frameworks={frameworks || []}
                        locations={locations || []}
                        advFilterCount={0}
                        hideAdvancedFilters={true}
                        kpiList={[]}
                        filterSteps={[]}
                        onFilterStepsChange={() => { }}
                        lockedSteps={[]}
                        rangeInput={rangeInput}
                        rangeError={rangeError}
                        onRangeInputChange={setRangeInput}
                        onRangeClear={handleRangeClear}
                        onRangeApply={handleRangeApply}
                    />

                    <BulkActionBar
                        count={selectedIds.length}
                        onAction={handleBulkAction}
                        onDeselect={handleDeselectAll}
                        hasPermission={bulkHasPermission}
                        disabledActions={!canCreate ? ["prefill"] : []}
                        disabledReasons={{
                            prefill: "You don't have permission to prefill qualitative data",
                        }}
                    />

                    <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px" }}>
                        <div style={{
                            background: "var(--surface)",
                            borderRadius: 12,
                            border: "1px solid var(--rule)",
                            overflow: "hidden",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.05)",
                        }}>
                            {/* ── Column headers ── */}
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 0",
                                background: "var(--surface-3)",
                                borderBottom: "2px solid #e5e8ed",
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                            }}>
                                <div style={{ width: 46, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                                    <input
                                        type="checkbox"
                                        className="kpi-checkbox"
                                        checked={isAllSelected}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const next = {};
                                                filteredQuestions.forEach(q => next[q.questionId || q.id] = true);
                                                setSelectedRows(next);
                                            } else {
                                                setSelectedRows({});
                                            }
                                        }}
                                    />
                                </div>
                                <div style={{ width: 32, flexShrink: 0 }} />
                                <ColLabel flex={1}>QUESTION</ColLabel>
                                <ColLabel width={120}>STATUS</ColLabel>
                                {!assignmentsForbidden && (
                                    <>
                                        <ColLabel width={150}>ASSIGNED TO</ColLabel>
                                        <ColLabel width={130}>ASSIGNED BY</ColLabel>
                                    </>
                                )}
                                <div style={{ width: 44, flexShrink: 0 }} />
                            </div>

                            {accessDecisionError && (
                                <div role="alert" style={{ margin: "0 20px 12px", padding: "10px 14px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: 12.5 }}>
                                    {accessDecisionError}
                                </div>
                            )}
                            {isLoading ? (
                                <div style={{ padding: "40px", textAlign: "center", color: "var(--ink-4)" }}>Loading questions...</div>
                            ) : filteredQuestions.length === 0 ? (
                                <div style={{ padding: "40px", textAlign: "center", color: "var(--ink-4)" }}>No tabular questions found.</div>
                            ) : (
                                filteredQuestions.map((question, index) => {
                                    const rowId = question.questionId || question.id;
                                    return (
                                        <QualitativeRow
                                            key={rowId}
                                            index={index}
                                            question={question}
                                            financialYearId={question.financialYearId}
                                            searchQuery={searchQuery}
                                            answered={answeredIds.has(Number(rowId))}
                                            expanded={!!expandedRows[rowId]}
                                            onExpand={() => toggleRowExpand(rowId)}
                                            selected={!!selectedRows[rowId]}
                                            onToggle={() => toggleRowSelection(rowId)}
                                            onAddData={handleAddData}
                                            onPrefill={(id, refresh) => setPrefillRow({ id, refresh })}
                                            showAssignments={!assignmentsForbidden}
                                            isEven={index % 2 === 0}
                                            lock={lockFor(locks, rowId)}
                                            accessRequested={hasPendingRequest(requests, rowId, null)}
                                            accessDeclined={wasAccessDeclined(requests, rowId, null)}
                                            requestingAccess={requestingAccessId === rowId}
                                            onRequestAccess={handleRequestAccess}
                                            canDecideAccess={canDecideAccess}
                                            pendingRequestIds={pendingRequestIds(requests, rowId)}
                                            onApproveAccess={handleApproveAccess}
                                            onRejectAccess={handleRejectAccess}
                                        />
                                    );
                                })
                            )}
                        </div>
                    </div>
                </main>

                <QualitativeAddDataModal
                    show={modalConfig.show}
                    question={modalConfig.question}
                    matchedAnswer={modalConfig.matchedAnswer}
                    financialYearId={financialYearId}
                    onHide={closeAddDataModal}
                    onSave={modalConfig.onSaveComplete}
                />

                <QualitativeOwnerAssignModal
                    show={bulkModal === "assign"}
                    onHide={closeBulkModal}
                    selectedIds={selectedIds}
                    financialYearId={financialYearId}
                    selectedQuestions={questions.filter(q => selectedIds.includes(Number(q.questionId || q.id)))}
                    onSuccess={() => { handleDeselectAll(); fetchQuestions({ background: true }); }}
                    onAssignmentsChanged={() => fetchQuestions({ background: true })}
                />

                <BulkReassignModal
                    show={bulkModal === "reassign"}
                    onHide={closeBulkModal}
                    selectedIds={selectedIds}
                    financialYearId={financialYearId}
                    managementListValue={managementListValue}
                    onSuccess={() => { handleDeselectAll(); fetchQuestions(); }}
                />

                <BulkPrefillModal
                    show={bulkModal === "prefill" || !!prefillRow}
                    onHide={() => { closeBulkModal(); setPrefillRow(null); }}
                    selectedIds={prefillRow ? [Number(prefillRow.id)] : selectedIds}
                    financialYears={financialYears}
                    selectedFinancialYearId={financialYearId}
                    onSuccess={() => {
                        prefillRow?.refresh?.();
                        handleDeselectAll();
                        fetchQuestions();
                    }}
                    type="qualitative"
                />

                <BulkReminderModal
                    show={bulkModal === "reminder"}
                    onHide={closeBulkModal}
                    selectedIds={selectedIds}
                    onSuccess={() => handleDeselectAll()}
                />

                <BulkAuditAssignModal
                    show={bulkModal === "auditAssign"}
                    onHide={closeBulkModal}
                    selectedIds={selectedIds}
                    financialYearId={financialYearId}
                    tabular={true}
                    companyId={companyId}
                    managementListValue={auditorType}
                    reportingQuestions={questions}
                    onSuccess={() => { handleDeselectAll(); fetchQuestions(); }}
                />
            </div>
        </MasterDataProvider>
    );
};

export default Qualitative;
